import { metresBetween } from "@trip/domain/poi";
import type { PaletteColor } from "@trip/domain/palette";
import { useMemo, useState } from "react";
import {
  MAX_QUERY,
  distanceSaid,
  onTripAlready,
  searchFailed,
  searchPlaces,
  searchable,
  STATUS_SAID,
  type PlaceHit,
} from "../lib/place-search.ts";
import { placesNamed, type Poi, type PoiCategory } from "../lib/pois.ts";
import { Pill } from "../ui/index.ts";
import { FoundPlaceView } from "./FoundPlaceView.tsx";
import { PoiRow, poiName } from "./PoiRow.tsx";

/**
 * Layer 2's search: a field along the very top of the map, over the category chips, with the
 * matches standing in the sheet's row, over the map. It finds two things, in two groups, in this
 * order:
 *
 *   1. **On this trip** — the trip's own places by name, live as you type, on the list already
 *      loaded. Tapping one opens that place as a tap on its pin does, and the search lets go of
 *      its words, so the place view and the map are what is left in sight.
 *   2. **From Google** — a place the trip does not have yet, on Enter and never on a keystroke:
 *      each of those is a billed call at the server's Google key, so one press is one search.
 *      Tapping a result opens it in a sheet shaped like the place view, built from what the
 *      search already returned, so looking through results costs nothing. The matches come off
 *      the surface while it is open, because they fill the map area on the phone and the map is
 *      what is worth seeing behind a place; Close puts them back, off the same answer, so going
 *      through a search spends on it once. Its Add carries the place's id to the Places page,
 *      where it is read once and the form opens on it.
 *
 * One field, no second control. The trip's own places cost nothing to filter, so they follow the
 * words; Google costs money, so it waits for Enter. The field being the only one in its form is
 * what makes a browser submit on Enter — a second field here would quietly end the search.
 *
 * "From Google" and not "Near you": the search leans towards the middle of the map, but Google
 * ignores that lean when the words name a place themselves ("ramen in Tokyo"), so a promise of
 * nearness would sometimes be a lie. Each row says how far it is instead, which is measured.
 *
 * A place the trip already has is never offered as something to add — see `onTripAlready` for the
 * rule and the distance. Holds its own words, so it starts empty each time the layer is entered.
 * A row offers no like and no visited tick: it is a way to a place, and the place view is where
 * those live.
 *
 * **Searching is a mode.** The moment the field takes focus the map's furniture stands down —
 * the chips, the layer switcher, the ranked sheet — and the matches have the surface to
 * themselves. That is what stops them being painted over, and it is keyed to focus rather than
 * to the field holding words: tied to the words, the chips would flap back the instant the box
 * was cleared. Close is the way out, and it sits outside the form, because a browser only
 * submits on Enter while the form holds exactly one field.
 *
 * With focus but nothing typed there is nothing to match yet, so the surface falls through: the
 * places opened lately, then the nearest ones, then — only when the trip has none at all — a
 * line saying what the box is for. One cascade rather than a special case per trip.
 */
/** How many nearest places the prefill offers before it would become a scroll of its own. */
const NEAREST_SHOWN = 8;

export function PoiSearch({
  tripId,
  pois,
  categories,
  chips = true,
  full = false,
  recent = [],
  centre = null,
  disabled = false,
  onSearching,
  onOpen,
  onAdd,
  onLook,
  me,
  colorOf,
}: {
  tripId: string;
  pois: Poi[];
  /** When the surface shows each name after its category's emoji (the phone's). */
  categories?: PoiCategory[] | undefined;
  /**
   * The category chips are showing under the bar, so a desktop's card starts below them. The
   * phone never needs it: searching takes the chips down.
   */
  chips?: boolean | undefined;
  /**
   * The matches take the whole map area rather than standing on it as a card — the phone, where
   * the furniture stands down with them. A desktop has the room and keeps its card.
   */
  full?: boolean | undefined;
  /** The trip's places opened lately on this device, newest first; the first prefill group. */
  recent?: Poi[] | undefined;
  /** The middle of the map, as [lon, lat], to lean the search towards; null until it is known. */
  centre?: [number, number] | null | undefined;
  /** Nothing can be opened or added right now (the editor is up). */
  disabled?: boolean;
  onOpen: (id: string) => void;
  /** Searching started or ended: the page stands its map furniture down while it is on. */
  onSearching?: ((on: boolean) => void) | undefined;
  /** A result the member chose to add: its id is carried to the Places page's form. */
  onAdd: (hit: PlaceHit) => void;
  /**
   * A result opened in the sheet: the map goes to it. Google's answers are not bounded by what
   * the map is showing -- the words can name a place anywhere -- so without this the sheet can
   * describe somewhere thousands of kilometres off screen.
   */
  onLook?: ((hit: PlaceHit) => void) | undefined;
  me: string | null;
  colorOf?: ((userId: string) => PaletteColor | null) | undefined;
}) {
  const [query, setQuery] = useState("");
  /** The field has focus: the map's furniture is down and this surface is showing. */
  const [focused, setFocused] = useState(false);
  /**
   * The last answer Google gave, and the words it was asked for. Kept against those words so that
   * typing on moves off an answer rather than leaving it standing under different words; typing
   * them again shows it again and spends nothing.
   */
  const [answer, setAnswer] = useState<{
    query: string;
    items: PlaceHit[] | null;
    failed: { text: string; unconfigured: boolean } | null;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  /** The result being looked at in the sheet, over the matches; null while none is. */
  const [viewing, setViewing] = useState<PlaceHit | null>(null);

  const emojis = categories && new Map(categories.map((c) => [c.id, c.emoji]));
  /**
   * The nearest places to the middle of the map, for the prefill. Ones already listed as recent
   * are left out rather than printed a second time under another heading.
   */
  const nearest = useMemo(() => {
    const already = new Set(recent.map((p) => p.id));
    const rest = pois.filter((p) => !already.has(p.id));
    if (centre === null) return rest.slice(0, NEAREST_SHOWN);
    const [lon, lat] = centre;
    return [...rest]
      .sort((a, b) => metresBetween(lat, lon, a.lat, a.lon) - metresBetween(lat, lon, b.lat, b.lon))
      .slice(0, NEAREST_SHOWN);
  }, [pois, recent, centre]);
  const matches = placesNamed(pois, query);
  const asked = query.trim() !== "";
  /** The words as the endpoint would take them, or null when it would refuse them. */
  const sendable = searchable(query);
  const shown = answer !== null && answer.query === sendable ? answer : null;
  /** What Google found that the trip does not already have: the only rows worth offering. */
  const offered = (shown?.items ?? []).filter((h) => !onTripAlready(h, pois));
  const everyOneHere = (shown?.items?.length ?? 0) > 0 && offered.length === 0;

  async function ask() {
    if (sendable === null || busy || disabled) return;
    setBusy(true);
    setAnswer(null);
    setViewing(null);
    try {
      setAnswer({
        query: sendable,
        items: await searchPlaces(tripId, sendable, centre),
        failed: null,
      });
    } catch (e) {
      console.error("places: the search for a new place did not come back", e);
      setAnswer({ query: sendable, items: null, failed: searchFailed(e) });
    } finally {
      setBusy(false);
    }
  }

  /** Enters the mode: the page takes its map furniture down. Idempotent. */
  function enter() {
    if (focused) return;
    setFocused(true);
    onSearching?.(true);
  }

  /** Leaves the mode: the map and its furniture come back. */
  function leave() {
    setFocused(false);
    setViewing(null);
    onSearching?.(false);
  }

  /** A way to a place: opening one lets go of the words and leaves the mode behind it. */
  const openPoi = (id: string) => {
    setQuery("");
    leave();
    onOpen(id);
  };

  const rowsOf = (list: Poi[], label: string) => (
    <ul className="plain poi-rows" aria-label={label}>
      {list.map((p) => (
        <PoiRow
          key={p.id}
          poi={p}
          name={poiName(p, emojis)}
          visited={p.ticked !== null}
          open={{ label: "Open", run: openPoi }}
          me={me}
          colorOf={colorOf}
        />
      ))}
    </ul>
  );

  /** One line under the group's heading: said, not alarming, where Google is simply not set up. */
  const says = (text: string) => (
    <p className="muted small" role="status" aria-label="From Google">
      {text}
    </p>
  );

  return (
    <>
      <div
        className="poi-search"
        data-chips={chips ? "true" : "false"}
        data-searching={focused && full ? "true" : undefined}
      >
        <div className="poi-search-row">
          <form
            className="poi-search-form"
            onSubmit={(e) => {
              e.preventDefault();
              void ask();
            }}
          >
            <label className="field">
              <span className="sr">Search places</span>
              <input
                type="search"
                placeholder="Search places"
                autoComplete="off"
                enterKeyHint="search"
                maxLength={MAX_QUERY}
                disabled={disabled}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  enter();
                }}
                onFocus={enter}
              />
            </label>
          </form>
          {focused && (
            <button
              type="button"
              className="button small poi-search-close"
              aria-label="Close search"
              onClick={leave}
            >
              Close
            </button>
          )}
        </div>
      </div>
      {focused && viewing === null && (
        <div
          className="poi-search-results"
          data-chips={chips ? "true" : "false"}
          data-full={full ? "true" : undefined}
        >
          {!asked && (
            <>
              {recent.length > 0 && (
                <div className="poi-search-group">
                  <p className="label poi-search-head">Recently opened</p>
                  {rowsOf(recent, "Recently opened")}
                </div>
              )}
              {nearest.length > 0 && (
                <div className="poi-search-group">
                  <p className="label poi-search-head">Nearest</p>
                  {rowsOf(nearest, "Nearest")}
                </div>
              )}
              {pois.length === 0 && (
                <p className="muted small" role="status">
                  This trip has no places yet. Type a name and press Enter to find one on Google,
                  and add it to the trip.
                </p>
              )}
            </>
          )}
          {asked && (
            <>
              <div className="poi-search-group">
                <p className="label poi-search-head">On this trip</p>
                {matches.length > 0 ? (
                  <ul className="plain poi-rows" aria-label="On this trip">
                    {matches.map((p) => (
                      <PoiRow
                        key={p.id}
                        poi={p}
                        name={poiName(p, emojis)}
                        visited={p.ticked !== null}
                        open={{ label: "Open", run: openPoi }}
                        me={me}
                        colorOf={colorOf}
                      />
                    ))}
                  </ul>
                ) : (
                  <p className="muted small" role="status">
                    {`No place matches “${query.trim()}”.`}
                  </p>
                )}
              </div>
              {(busy || shown !== null || sendable !== null) && (
                <div className="poi-search-group">
                  <p className="label poi-search-head">From Google</p>
                  {!busy &&
                    shown === null &&
                    says("Press Enter to look for a place the trip has not got yet.")}
                  {busy && says("Searching…")}
                  {shown?.failed &&
                    (shown.failed.unconfigured ? (
                      says(shown.failed.text)
                    ) : (
                      <p className="error small" role="alert">
                        {shown.failed.text}
                      </p>
                    ))}
                  {offered.length > 0 && (
                    <ul className="plain poi-rows" aria-label="From Google">
                      {offered.map((h) => {
                        const flag = h.status ? STATUS_SAID[h.status] : undefined;
                        const far = h.distanceM === undefined ? null : distanceSaid(h.distanceM);
                        return (
                          <li key={h.placeId} className="poi-row poi-found" aria-label={h.name}>
                            <div className="poi-row-text">
                              <button
                                type="button"
                                className="poi-row-open"
                                aria-label={`Open ${h.name}`}
                                disabled={disabled}
                                onClick={() => {
                                  setViewing(h);
                                  onLook?.(h);
                                }}
                              >
                                <strong>{h.name}</strong>
                              </button>
                            </div>
                            {(flag || far) && (
                              <div className="poi-row-foot">
                                {flag && <Pill tone="wait">{flag}</Pill>}
                                {far && <span className="small muted">{far}</span>}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {everyOneHere && says("Every place Google found is already on this trip.")}
                  {shown?.items?.length === 0 &&
                    says(`Google found no place called “${sendable}”.`)}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {viewing && (
        <FoundPlaceView
          key={viewing.placeId}
          hit={viewing}
          onAdd={() => onAdd(viewing)}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}
